import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { styled as muiStyled } from '@mui/material/styles';
import TooltipSimple from '../tooltips/TooltipSimple.jsx';
import { Checkbox, ClickAwayListener, Grow, List, ListItemText, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { backYouTheme } from '../../../../theme.ts';
import styled from 'styled-components';

const Dropdown = styled.div`
	position: relative;
	.dropdown-item.active, .dropdown-item:active {
		background-color: #F0F0F0
	}
`;

const Menu = muiStyled(Paper)({
	border: '1px solid #aaaaaaa3',
	borderRadius: '0.38rem'
});

export default function DropdownSelect(props) {
	const {
		parentFunction,
		icon = null, // classes font-awesome Ex: fa-regular fa-bars-sort
		title,
		items,
		selectedItems = [],
		disabled = false,
		displayChevron = true,
		btnClass = 'btn-gc-light-blue-dark text-gc-dark-blue font-weight-bold',
		autoTrad = true
	} = props;

	const { t: gctrad } = useTranslation();

	const [open, setOpen] = useState(false);
	const anchorRef = useRef(null);
	const prevOpen = useRef(open);

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target)) {
			return;
		}
		setOpen(false);
	};

	const handleListKeyDown = (event) => {
		if (event.key === 'Tab') {
			event.preventDefault();
			setOpen(false);
		} else if (event.key === 'Escape') {
			setOpen(false);
		}
	}

	useEffect(() => {
		if (prevOpen.current === true && open === false) {
			anchorRef.current.focus();
		}
		prevOpen.current = open;
	}, [open]);

	let finalBtnClass = btnClass + " btn w-100 w-md-auto" + (disabled ? ' disabled' : '');

	const DropdownTheme = useMemo(() =>
		createTheme({
			palette: {
				primary: backYouTheme.palette.primary,
				secondary: backYouTheme.palette.secondary
			},
			typography: backYouTheme.typography
		}),
		[backYouTheme]);

	return (
		<ThemeProvider theme={DropdownTheme}>
			<Dropdown className="dropdown">
				<button
					ref={anchorRef}
					className={finalBtnClass}
					aria-haspopup="true"
					aria-expanded={open ? 'true' : undefined}
					onClick={handleToggle}
				>
					{icon &&
						<span className="mr-md-2">
							<i className={icon}></i>
						</span>
					}

					<span className="d-none d-md-inline-block">
						{title}
					</span>

					{displayChevron && <span className="ml-2 ml-md-4">
						{open == true ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />}
					</span>}
				</button>
				{selectedItems.length > 0 && (
					<TooltipSimple
						title={gctrad('Supprimer la sélection')}
						placement={'top'}
						style={{
							position: 'absolute',
							top: '-0.8rem',
							right: 0
						}}
					>
						<button
							onClick={!disabled ? () => parentFunction(null) : () => false}
							className="btn btn-link text-gc-dark-blue font-weight-bold m-0"
						>
							<i className="fa-solid fa-circle-xmark"></i>
						</button>
					</TooltipSimple>
				)}

				<Popper
					open={open}
					anchorEl={anchorRef.current}
					role={undefined}
					placement="bottom-end"
					transition
					disablePortal
					sx={{ zIndex: 10000 }}
				>
					{({ TransitionProps, placement }) => (
						<Grow
							{...TransitionProps}
							style={{
								marginTop: 3,
								marginBottom: 3,
								transformOrigin: 'right top'
							}}
						>
							<Menu elevation={0}>
								<ClickAwayListener onClickAway={handleClose}>
									<MenuList
										className="text-gc-dark-blue"
										autoFocusItem={open}
										onKeyDown={handleListKeyDown}
									>
										<List
											sx={{
												width: '100%',
												padding: 0,
												position: 'relative',
												overflow: 'auto',
												maxHeight: 400,
												'::-webkit-scrollbar': {
													'width': '.4rem',
													'height': '.4rem'
												},
												'::-webkit-scrollbar-thumb': {
													'backgroundColor': '#AAAAAA',
													'borderRadius': '50px'
												}
											}}
										>
											{items.map((e, index) => {
												let label = autoTrad ? (e.label ? e.label.trad() : e.trad()) : e.label;
												return (
													<MenuItem
														dense
														disableGutters
														key={index}
														sx={{
															padding: '0 1rem 0 .3rem',
															color: backYouTheme.palette.primary.main
														}}
														onClick={!disabled ? () => parentFunction(e) : () => false}
													>
														<Checkbox checked={selectedItems.filter((i) => i.value == e.value).length > 0} />
														<ListItemText primary={label} />
													</MenuItem>
												);
											})}
										</List>
									</MenuList>
								</ClickAwayListener>
							</Menu>
						</Grow>
					)}
				</Popper>
			</Dropdown>
		</ThemeProvider>
	);
}